import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mt-2">抱歉，您要找的漫画不存在或已下架</p>
          <Link to="/" className="mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate,
    dateModified: comic.chapters[0]?.updateDate,
    publisher: { '@type': 'Organization', name: '韩漫画网' },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫画网 | 免费在线漫画阅读`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}... 韩漫画网正版连载，${comic.updateDay}。`}
        keywords={`${comic.title}, ${comic.category}漫画, ${comic.tags.join(', ')}, 韩漫画网`}
        canonicalUrl={`https://www.hanmanhua.com/article/${comic.id}`}
        ogType="article"
        schemaJson={schemaJson}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: `${cat?.name || ''}漫画`, href: `/category/${comic.category}` },
                  { label: comic.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-[240px] aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 漫画封面`}
                    title={`${comic.title} - 韩漫画网正版漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">{tag}</span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
                  <div>
                    <span className="text-xs text-foreground-500">作者</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{comic.author}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">画师</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{comic.artist}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">评分</span>
                    <p className="text-sm font-bold text-primary-600 mt-0.5">{comic.rating} 分</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">阅读量</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{formatViews(comic.views)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">更新日</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">{comic.updateDay}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">总章节</span>
                    <p className="text-sm font-medium text-foreground-800 mt-0.5">共 {comic.chapters.length} 话</p>
                  </div>
                </div>

                <div className="mt-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Link
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </Link>
                  <button className="flex items-center justify-center gap-2 px-8 py-3 bg-background-100 border border-background-200 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line"></i> 收藏漫画
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-6">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">章节目录</h2>
            <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto">
                {comic.chapters.slice(0, 50).map((chapter, index) => (
                  <div
                    key={chapter.id}
                    className={`flex items-center justify-between px-4 py-3 border-b border-background-100 last:border-b-0 hover:bg-background-100 transition-colors cursor-pointer ${
                      chapter.isFree ? '' : 'opacity-80'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-foreground-400 w-10 flex-shrink-0">{index + 1}</span>
                      <span className="text-sm text-foreground-800 truncate">{chapter.title}</span>
                      {chapter.isFree && (
                        <span className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 text-xs font-medium flex-shrink-0">免费</span>
                      )}
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-4">{chapter.updateDate}</span>
                  </div>
                ))}
              </div>
              {comic.chapters.length > 50 && (
                <div className="text-center py-3 bg-background-50 border-t border-background-200">
                  <button className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                    展开全部 {comic.chapters.length} 话 <i className="ri-arrow-down-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-10">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} {...rc} />
                ))}
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}